"use client";

import { c as _c } from "react/compiler-runtime";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { getDefaultQueryRetry, WAVE_DROPS_PARAMS } from "@/components/react-query-wrapper/utils/query-utils";
import type { ApiDropsLeaderboardPage } from "@/generated/models/ApiDropsLeaderboardPage";
import { generateUniqueKeys, mapToExtendedDrops } from "@/helpers/waves/wave-drops.helpers";
import { commonApiFetch } from "@/services/api/common-api";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo } from "react";
export enum WaveDropsLeaderboardSort {
  RANK = "RANK",
  PRICE = "PRICE",
  RATING_PREDICTION = "RATING_PREDICTION",
  TREND = "TREND",
  MY_REALTIME_VOTE = "MY_REALTIME_VOTE",
  CREATED_AT = "CREATED_AT",
}
interface UseWaveDropsLeaderboardProps {
  readonly waveId: string;
  readonly sort?: WaveDropsLeaderboardSort | undefined;
  readonly enabled?: boolean | undefined;
  readonly curatedByGroupId?: string | undefined;
  readonly minPrice?: number | undefined;
  readonly maxPrice?: number | undefined;
  readonly priceCurrency?: string | undefined;
}
const SORT_DIRECTION_MAP: Record<WaveDropsLeaderboardSort, "ASC" | "DESC" | undefined> = {
  [WaveDropsLeaderboardSort.RANK]: undefined,
  [WaveDropsLeaderboardSort.PRICE]: undefined,
  [WaveDropsLeaderboardSort.RATING_PREDICTION]: "DESC",
  [WaveDropsLeaderboardSort.TREND]: "DESC",
  [WaveDropsLeaderboardSort.MY_REALTIME_VOTE]: undefined,
  [WaveDropsLeaderboardSort.CREATED_AT]: "DESC"
};
export function useWaveDropsLeaderboard(t0) {
  const $ = _c(60);
  const {
    waveId,
    sort: t1,
    enabled: t2,
    curatedByGroupId,
    minPrice,
    maxPrice,
    priceCurrency
  } = t0;
  const sort = t1 === undefined ? WaveDropsLeaderboardSort.RANK : t1;
  const enabled = t2 === undefined ? true : t2;
  const queryClient = useQueryClient();
  const sortDirection = SORT_DIRECTION_MAP[sort];
  const isQueryEnabled = enabled && !!waveId;
  let t3;
  if ($[0] !== curatedByGroupId) {
    t3 = curatedByGroupId?.trim() ?? undefined;
    $[0] = curatedByGroupId;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const normalizedCuratedByGroupId = t3;
  let t4;
  if ($[2] !== priceCurrency) {
    t4 = priceCurrency?.trim() ?? undefined;
    $[2] = priceCurrency;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const normalizedPriceCurrency = t4;
  const normalizedMinPrice = typeof minPrice === "number" && Number.isFinite(minPrice) && minPrice >= 0 ? minPrice : undefined;
  const normalizedMaxPrice = typeof maxPrice === "number" && Number.isFinite(maxPrice) && maxPrice >= 0 ? maxPrice : undefined;
  let normalizedPriceLower = normalizedMinPrice;
  let normalizedPriceUpper = normalizedMaxPrice;
  if (typeof normalizedPriceLower === "number" && typeof normalizedPriceUpper === "number" && normalizedPriceLower > normalizedPriceUpper) {
    normalizedPriceLower = normalizedMaxPrice;
    normalizedPriceUpper = normalizedMinPrice;
  }
  let t5;
  if ($[4] !== normalizedPriceLower) {
    t5 = typeof normalizedPriceLower === "number" ? normalizedPriceLower.toString() : undefined;
    $[4] = normalizedPriceLower;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== normalizedPriceUpper) {
    t6 = typeof normalizedPriceUpper === "number" ? normalizedPriceUpper.toString() : undefined;
    $[6] = normalizedPriceUpper;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== normalizedPriceCurrency || $[9] !== t5 || $[10] !== t6) {
    t7 = {
      normalizedPriceCurrency,
      normalizedPriceLower: t5,
      normalizedPriceUpper: t6
    };
    $[8] = normalizedPriceCurrency;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const canonicalPriceFilters = t7;
  const t8 = normalizedCuratedByGroupId ?? null;
  const t9 = canonicalPriceFilters.normalizedPriceLower ?? null;
  const t10 = canonicalPriceFilters.normalizedPriceUpper ?? null;
  const t11 = canonicalPriceFilters.normalizedPriceCurrency ?? null;
  let t12;
  if ($[12] !== sort || $[13] !== sortDirection || $[14] !== t10 || $[15] !== t11 || $[16] !== t8 || $[17] !== t9 || $[18] !== waveId) {
    t12 = [QueryKey.DROPS_LEADERBOARD, {
      waveId,
      page_size: WAVE_DROPS_PARAMS.limit,
      sort,
      sort_direction: sortDirection,
      curation_id: t8,
      min_price: t9,
      max_price: t10,
      price_currency: t11
    }];
    $[12] = sort;
    $[13] = sortDirection;
    $[14] = t10;
    $[15] = t11;
    $[16] = t8;
    $[17] = t9;
    $[18] = waveId;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  const queryKey = t12 as const;
  let t13;
  if ($[20] !== canonicalPriceFilters.normalizedPriceCurrency || $[21] !== canonicalPriceFilters.normalizedPriceLower || $[22] !== canonicalPriceFilters.normalizedPriceUpper || $[23] !== normalizedCuratedByGroupId) {
    t13 = t14 => {
      const {
        pageParam,
        pageSize,
        targetSort,
        targetSortDirection
      } = t14;
      const params = {
        page_size: pageSize.toString(),
        sort: targetSort
      };
      if (targetSortDirection) {
        params.sort_direction = targetSortDirection;
      }
      if (typeof pageParam === "number") {
        params.page = `${pageParam}`;
      }
      if (normalizedCuratedByGroupId) {
        params.curation_id = normalizedCuratedByGroupId;
      }
      if (canonicalPriceFilters.normalizedPriceLower) {
        params.min_price = canonicalPriceFilters.normalizedPriceLower;
      }
      if (canonicalPriceFilters.normalizedPriceUpper) {
        params.max_price = canonicalPriceFilters.normalizedPriceUpper;
      }
      if (canonicalPriceFilters.normalizedPriceCurrency) {
        params.price_currency = canonicalPriceFilters.normalizedPriceCurrency;
      }
      return params;
    };
    $[20] = canonicalPriceFilters.normalizedPriceCurrency;
    $[21] = canonicalPriceFilters.normalizedPriceLower;
    $[22] = canonicalPriceFilters.normalizedPriceUpper;
    $[23] = normalizedCuratedByGroupId;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  const buildLeaderboardParams = t13;
  let t14;
  if ($[25] !== buildLeaderboardParams || $[26] !== waveId) {
    t14 = async t15 => {
      const {
        pageParam: pageParam_0,
        pageSize: pageSize_0,
        targetSort: targetSort_0,
        targetSortDirection: targetSortDirection_0
      } = t15;
      return await commonApiFetch({
        endpoint: `waves/${waveId}/leaderboard`,
        params: buildLeaderboardParams({
          pageParam: pageParam_0,
          pageSize: pageSize_0,
          targetSort: targetSort_0,
          targetSortDirection: targetSortDirection_0
        })
      });
    };
    $[25] = buildLeaderboardParams;
    $[26] = waveId;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  const fetchLeaderboardPage = t14;
  let t15;
  if ($[28] !== sort) {
    t15 = lastPage => {
      if (sort === WaveDropsLeaderboardSort.MY_REALTIME_VOTE) {
        const haveZeroVotes = lastPage.drops.some(_temp);
        if (haveZeroVotes) {
          return null;
        }
      }
      return lastPage.next ? lastPage.page + 1 : null;
    };
    $[28] = sort;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  const getNextPageParam = t15;
  let t16;
  if ($[30] !== fetchLeaderboardPage || $[31] !== sort || $[32] !== sortDirection) {
    t16 = async t17 => {
      const {
        pageParam: pageParam_1
      } = t17;
      return await fetchLeaderboardPage({
        pageParam: pageParam_1,
        pageSize: WAVE_DROPS_PARAMS.limit,
        targetSort: sort,
        targetSortDirection: sortDirection
      });
    };
    $[30] = fetchLeaderboardPage;
    $[31] = sort;
    $[32] = sortDirection;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = getDefaultQueryRetry();
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== getNextPageParam || $[36] !== isQueryEnabled || $[37] !== queryKey || $[38] !== t16) {
    t18 = {
      queryKey,
      queryFn: t16,
      initialPageParam: null,
      getNextPageParam,
      enabled: isQueryEnabled,
      staleTime: 60000,
      ...t17
    };
    $[35] = getNextPageParam;
    $[36] = isQueryEnabled;
    $[37] = queryKey;
    $[38] = t16;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetching,
    isFetchingNextPage,
    refetch
  } = useInfiniteQuery(t18);
  let t19;
  if ($[40] !== data || $[41] !== sort) {
    bb0: {
      if (!data?.pages) {
        let t20;
        if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
          t20 = [];
          $[43] = t20;
        } else {
          t20 = $[43];
        }
        t19 = t20;
        break bb0;
      }
      const mappedDrops = mapToExtendedDrops(data.pages.map(_temp2), []);
      const uniqueDrops = generateUniqueKeys(mappedDrops, []);
      if (sort === WaveDropsLeaderboardSort.MY_REALTIME_VOTE) {
        t19 = uniqueDrops.filter(_temp3);
        break bb0;
      }
      t19 = uniqueDrops;
    }
    $[40] = data;
    $[41] = sort;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  const drops = t19;
  const hasInitialized = !!data?.pages;
  let t20;
  let t21;
  if ($[44] !== queryClient || $[45] !== waveId) {
    t20 = () => () => {
      queryClient.removeQueries({
        queryKey: [QueryKey.DROPS, {
          waveId
        }]
      });
    };
    t21 = [waveId, queryClient];
    $[44] = queryClient;
    $[45] = waveId;
    $[46] = t20;
    $[47] = t21;
  } else {
    t20 = $[46];
    t21 = $[47];
  }
  useEffect(t20, t21);
  let t22;
  if ($[48] !== fetchNextPage || $[49] !== hasNextPage) {
    t22 = async () => {
      if (hasNextPage) {
        await fetchNextPage();
      }
    };
    $[48] = fetchNextPage;
    $[49] = hasNextPage;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  const manualFetch = t22;
  const t23 = isQueryEnabled && (isFetching || !hasInitialized);
  let t24;
  if ($[51] !== drops || $[52] !== fetchNextPage || $[53] !== hasNextPage || $[54] !== isError || $[55] !== isFetchingNextPage || $[56] !== manualFetch || $[57] !== refetch || $[58] !== t23) {
    t24 = {
      drops,
      fetchNextPage,
      hasNextPage,
      isError,
      isFetching: t23,
      isFetchingNextPage,
      refetch,
      manualFetch
    };
    $[51] = drops;
    $[52] = fetchNextPage;
    $[53] = hasNextPage;
    $[54] = isError;
    $[55] = isFetchingNextPage;
    $[56] = manualFetch;
    $[57] = refetch;
    $[58] = t23;
    $[59] = t24;
  } else {
    t24 = $[59];
  }
  return t24;
}
function _temp3(drop_0) {
  return drop_0.context_profile_context?.rating !== 0;
}
function _temp2(page) {
  return {
    wave: page.wave,
    drops: page.drops
  };
}
function _temp(drop) {
  return drop.context_profile_context?.rating === 0;
}